import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { IonContent, Platform, AlertController } from '@ionic/angular';
import { Navigator } from 'src/app/components/menu/navigator';
import { CustomerModel } from 'src/app/models/customer-model';
import { AppLinksService } from 'src/app/services/applinks/app-links.service';
import { AppstorageTsService } from 'src/app/services/appstorage/appstorage.ts.service';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { BoydRewardsService } from 'src/app/services/boyd-rewards/boyd-rewards.service';
import { isUndefined } from 'src/app/services/codeUtils';
import { ConfigService } from 'src/app/services/config/config.service';
import { GlobalErrorHandlerService } from 'src/app/services/global-errors/global-error-handler.service';
import { LoginService } from 'src/app/services/login/login.service';
import { GalleryService } from 'src/app/services/gallery/gallery.service';
import { UserSessionService } from 'src/app/services/user-session/user-session.service';
import { ProgressingComponent } from '../progressing/progressing.component';
import { BiometricService } from 'src/app/services/biometric/biometric.service';
import { EventsService } from 'src/app/services/event/event.service';
import { mustBeTruthy } from 'src/app/services/customFormValidators';

@Component({
	selector: 'app-boyd-rewards-options',
	templateUrl: './boyd-rewards-options.component.html',
	styleUrls: ['./boyd-rewards-options.component.scss'],
})
export class BoydRewardsOptionsComponent implements OnInit {
	@ViewChild(ProgressingComponent) progressingUI!: ProgressingComponent;
	@ViewChild(IonContent, { static: false }) content: IonContent;
	@Input() biometricVerificationFailed = false;

	public clientapp = new FormControl('')
	public csrf = new FormControl('');
	public certOver18 = new FormControl(false, [mustBeTruthy]);
	public username = new FormControl('', [
		Validators.required,
		this.BoydCardNumberOrUsernameValidator(),
		Validators.maxLength(50),
		Validators.minLength(2)
	]);
	public password = new FormControl('');

	public isCustomerHasEmail: boolean = false;
	public customer: CustomerModel;
	public showPassword: boolean;
	public doLoginInProgress: boolean = false;
	public email: string;
	public error: GlobalErrorHandlerService = null;
	public loginForm: FormGroup;
	public buttonDisabled: boolean = false;
	public isPlayerLoggedInAsGuest: boolean = false;
	public enabledBoydConnect: boolean;
	public enabledBoydApplyCard: boolean;
	public thirdPartyAppsExternalID: boolean = false;
	featureTermsAndConditionURL: any;
	featurePrivacyURL: any;

	constructor(
		private readonly events: EventsService,
		private navigator: Navigator,
		private userSessionService: UserSessionService,
		private appLinks: AppLinksService,
		private biometricService: BiometricService,
		private galleryService: GalleryService,
		private loginService: LoginService,
		private globalErrorHandlerService: GlobalErrorHandlerService,
		private appStorageService: AppstorageTsService,
		private authenticationService: AuthenticationService,
		private boydRewardsService: BoydRewardsService,
		private config: ConfigService,
		public alertController: AlertController,
		public platform: Platform,
	) { }

	ngOnInit() {

		this.loginForm = new FormGroup({
			csrf: this.csrf,
			username: this.username,
			password: this.password,
			clientapp: this.clientapp,
			certOver18: this.certOver18
		});
		this.generateCsrfToken();

		this.enabledBoydApplyCard = this.config.getConfig().FEATURE_ENABLE_BOYD_APPLY_CARD ? (this.config.getConfig().FEATURE_ENABLE_BOYD_APPLY_CARD === '1' ? true : false) : true;
		this.enabledBoydConnect = this.config.getConfig().FEATURE_ENABLE_BOYD_CONNECT_CARD ? (this.config.getConfig().FEATURE_ENABLE_BOYD_CONNECT_CARD === '1' ? true : false) : true;

		this.userSessionService.getCustomer().subscribe(
			(customer) => {
				if (customer) {
					this.customer = customer;
					this.isCustomerHasEmail = !isUndefined(customer.email);
				} else {
					this.isCustomerHasEmail = false;
					this.thirdPartyAppsExternalID = false;
				}
			}, (err) => {
				console.error("getCustomer", err);
				this.isCustomerHasEmail = false;
				this.thirdPartyAppsExternalID = false;
			}
		);

		this.featureTermsAndConditionURL = this.config.getConfig().FEATURE_SITE_TERMS_LINK;
		this.featurePrivacyURL = this.config.getConfig().FEATURE_SITE_PRIVACY_LINK;

		//custom function to check if User has external ID to check 3rd party login
		this.checkThirdPartyExternalIDs();

		this.appStorageService.getPlayerAsGuest().then((isGuest) => {
			if (isGuest.value === 'true') return this.isPlayerLoggedInAsGuest = true;
			return this.isPlayerLoggedInAsGuest = false;
		})

		if (this.platform.is('capacitor') && !this.biometricVerificationFailed) {
			this.initiateBiometric();
		}
	}

	navigateTo(page: string, data) {
		return this.navigator.navigateTo(page, { customModalClass: 'crown-modal' }, data);
	}

	redirectBasedOnUserData() {
		if (this.isCustomerHasEmail) {
			return this.navigator.navigateTo("ApplyBoydRewardsStepTwoComponent", { customModalClass: 'crown-modal' });
		} else {
			return this.navigator.navigateTo("ApplyBoydRewardsStepOneComponent", { customModalClass: 'crown-modal' });
		}
	}

	async initiateBiometric() {
		this.biometricService.verificationError = null;
		await this.biometricService.isAvailable();
		await this.biometricService.verifyUser();
		this.doLoginBiometric()
	}

	private reverseTile(): any {
		return this.events.publish("events:animation:tile:reverse");
	}

	async doLoginBiometric() {
		this.reverseTile();
		if (this.biometricService.verificationError) {
			//verfication failed
			await this.alertCreator(
				{
					header: `${this.biometricService.biometricType} verification failed`,
					message: 'Would you like to remove your saved credentials?',
					buttons: [
						{
							text: 'Yes',
							handler: async () => {
								await this.biometricService.deleteCredentials();
								await this.biometricService.setBiometricStatus(false);
							},
						},
						{
							text: 'No',
							handler: async () => {
								await this.biometricService.setBiometricStatus(true);
								this.biometricService.verificationError = null;
							},
						},
					],
				}
			)
		}
		if (this.biometricService.verified) {
			this.loginForm.controls['password'].setValue(this.biometricService.credentials.password);
			this.loginForm.controls['username'].setValue(this.biometricService.credentials.username);
			this.biometricService.verified = false;
			this.doLogin()
		}
	}

	async alertCreator(alert) {
		const currentAlert = await this.alertController.create({
			header: alert.header,
			message: alert.message,
			buttons: alert.buttons
		});
		await currentAlert.dismiss();
		await currentAlert.present();
	}

	BoydCardNumberOrUsernameValidator(): ValidatorFn {
		return (control) => {
			const value = control.value;
			const maxUsernameCharacters = 50
			if (value) {
				if (value.length <= maxUsernameCharacters) {
					return null; // valid username check
				}
				else if (/^[A-Za-z0-9]{2,11}$/.test(value)) {
					return null; // valid boyd card number
				} else {
					return { invalidInput: true }; // Invalid input
				}
			}
			return null;
		};
	}

	generateCsrfToken() {
		this.authenticationService.getCsrfToken().subscribe((response) => {
			if (!!response.payload && !!response.payload.token) {
				this.loginForm.controls['csrf'].setValue(response.payload.token);
			}
		});
	}

	async doLogin() {
		this.doLoginInProgress = true;
		this.error = null;
		this.buttonDisabled = true;

		this.email = this.loginForm.get('username').value;

		if (!this.loginForm.valid) return console.log('Form validation failed');

		this.loginForm.get("clientapp").setValue(this.config.getClientApp());
		const value = this.loginForm.value;


		if (this.isPlayerLoggedInAsGuest || this.isCustomerHasEmail) {
			//Player has cognito login but is logged in as either Guest OR with social email/password
			const obj: any = {
				clientapp: this.config.getClientApp(),
				csrf: this.loginForm.get('csrf').value,
				session_token: this.userSessionService.getSessionToken(),
				customer_id: this.customer.customerId,
				reward_number: this.loginForm.get('username').value.trim(),
				password: this.loginForm.get('password').value,
				certOver18: true
			}

			const apiCall$ = this.boydRewardsService.callExternalConnect(obj);
			this.progressingUI.listenObservable(apiCall$);
			apiCall$.subscribe(
				(response: any) => {
					if (response && !!response.result) {
						this.buttonDisabled = false;
						if (response.result === 0) return this.doLoginFailProtocol(response.code);
						this.appStorageService.setPlayerAsGuest(false);
						this.userSessionService.refreshCustomer();
						this.navigator.dismissAll();
						this.navigator.navigateTo("AccountCreatedConfirmationComponent", { customModalClass: 'crown-modal' }, { boydRewardCognitoConnectFlag: true });
					}
				},
				(error) => {
					this.buttonDisabled = false;

					this.generateCsrfToken();
					this.error = error;
					this.doLoginFailProtocol(error.getErrorCode().toUpperCase());
				}
			);
		} else if (this.thirdPartyAppsExternalID) {
			//FB email isn't stored in BO so, adding externalID solution for all 3rd party login
			let obj: any = {
				clientapp: this.config.getClientApp(),
				csrf: this.loginForm.get('csrf').value,
				session_token: this.userSessionService.getSessionToken(),
				customer_id: this.customer.customerId,
				reward_number: this.loginForm.get('username').value.trim(),
				password: this.loginForm.get('password').value
			}
			const apiCall$ = this.boydRewardsService.callExternalConnect(obj);
			this.progressingUI.listenObservable(apiCall$);
			apiCall$.subscribe(
				(response: any) => {
					if (response && !!response.result) {
						this.buttonDisabled = false;
						if (response.result === 0) return this.doLoginFailProtocol(response.code);
						this.appStorageService.setPlayerAsGuest(false);
						this.userSessionService.refreshCustomer();
						this.navigator.dismissAll();
						this.navigator.navigateTo("AccountCreatedConfirmationComponent", { customModalClass: 'crown-modal' }, { boydRewardCognitoConnectFlag: true });
					}
				},
				(error) => {
					this.buttonDisabled = false;

					this.generateCsrfToken();
					this.error = error;
					this.doLoginFailProtocol(error.getErrorCode().toUpperCase());
				}
			);
		} else {
			//User is not logged in to system - Not a Guest OR not logged in using email/password
			//Normal Cognito Login
			const apiCall$ = this.loginService.loginCustomer(value);
			this.progressingUI.listenObservable(apiCall$);
			apiCall$.subscribe((response: any) => {
				this.doLoginInProgress = false;
				this.buttonDisabled = false;
				if (response.result === 0) return this.doLoginFailProtocol(response.code);
				this.userSessionService.setSessionToken(response.sessionToken);
				this.appStorageService.setPlayerAsGuest(false);
				this.loginService.setCustomerID(response.customerId);
				this.galleryService.getGameList();
				this.appStorageService.setPlayerPlatformID(response.platformId);
				this.loginService.setLoginResponseInStorage();
				if (this.biometricService.useBiometric) {
					this.biometricService.setCredentials(value);
				}
			},
				(error) => {
					this.buttonDisabled = false;
					this.error = error;
					this.doLoginFailProtocol(error.getErrorCode().toUpperCase());
				});
		}
	}

	doLoginFailProtocol(errorCode: string) {
		this.buttonDisabled = false;

		this.generateCsrfToken();
		this.content.scrollToTop(10);

		if (!this.error || !(this.error instanceof GlobalErrorHandlerService)) {
			this.error = this.globalErrorHandlerService;
		}

		switch (errorCode) {
			case 'LOGIN_WRONG_USERNAME':
				this.error.setErrorMessage('Sorry, an error has occurred. Please try again.');
				this.username.setErrors({ LOGIN_WRONG_USERNAME: true });
				this.username.markAsDirty();
				this.username.markAsTouched();
				break;
			case 'COGNITO_USER_NOTFOUND_ERROR':
				this.error.setErrorMessage('An error has occured logging you in. Please try again.');
				this.username.setErrors({ COGNITO_USER_NOTFOUND_ERROR: true });
				this.username.markAsDirty();
				this.username.markAsTouched();
				break;
			case 'LOGIN_WRONG_PASSWORD':
				this.error.setErrorMessage('Sorry, an error has occurred. Please try again.');
				this.password.setErrors({ LOGIN_WRONG_PASSWORD: true });
				this.password.markAsDirty();
				this.password.markAsTouched();
				break;
			case 'FORCE_PASSWORD_RESET':
				this.error.setErrorMessage("An e-mail has been sent to you. Please follow the instructions to reset your password.");
				//this.startMigrationProcess();
				break;
			case 'FAILED_TOO_MANY_ATTEMPTS':
				this.error.setErrorMessage("Too many Attemps, please contact support");
				break;
			case "LOGIN_MAX_LOGINS_REACHED":
				this.error.setErrorMessage("Your account was disabled upon too many failed logins, please contact support.");
				//this.tooManyLoginAttemps = true;
				break;
			case 'TIME_LIMIT_EXCEEDED':
				this.error.setErrorMessage('Time limit exceeded.');
				break;
			case 'NOT_AUTHORIZED_CLIENT_APP':
				this.error.setErrorMessage('You are logging from an unauthorized client app version.');
				break;
			case 'CUSTOMER_EXCLUDED_BY_AUTHORITY':
				this.error.setErrorMessage("Unfortunately, there is an error - you can’t play on this site.");
				break;
			case 'CUSTOMER_WRONG_STATUS':
				this.error.setErrorMessage("Your account is not in 'Active' status. Please contact support.");
				break;
			case 'CUSTOMER_SELF_EXCLUDED':
				this.navigator.toLobby();
				this.navigator.openPopover("PopoverSelfExcludeComponent", "", 'standard-popover');
				break;
			case 'CUSTOMER_SELF_EXCLUDED_COOL_OFF':
				this.error.setErrorMessage('You have suspended yourself from play.');
				break;
			case 'CUSTOMER_SUSPENDED':
				this.error.setErrorMessage('Your account has been temporarily suspended. If this is not resolved in 24 hours please contact support.');
				break;
			default:
				this.error.setErrorMessage('An error has occured logging you in. Please try again.');
				break;
		}

		//this.eventsService.publish('event:login:error', { errorCode: this.error.getErrorCode().toUpperCase(), errorName: this.error.getErrorMessage() });
	}

	public hidePassword(): boolean {
		this.showPassword = false;
		return;
	}

	public unhidePassword(): boolean {
		this.showPassword = true;
		return;
	}

	public goToBoydRewards() {
		const boydRewardsLink = this.config.getConfig()?.BOYD_REWARDS_WEBSITE ?? 'https://rewards.boydgaming.com/';
		return this.appLinks.openExternalPage(boydRewardsLink);
	}

	checkThirdPartyExternalIDs() {
		if (localStorage.getItem('userSession.sessionToken') !== null) {
			this.authenticationService.callGetAccount().subscribe(res => {
				if ('external_associations' in res.payload) {
					const extAssociations: any[] = res.payload.external_associations;
					for (const e in extAssociations) {
						if (extAssociations[e].external_system === '3' || extAssociations[e].external_system === '14' || extAssociations[e].external_system === '15') {
							this.thirdPartyAppsExternalID = true;
							break;
						}
					}
				} else {
					this.thirdPartyAppsExternalID = false;
				}
			}, (err) => {
				this.thirdPartyAppsExternalID = false;
			});
		} else {
			this.thirdPartyAppsExternalID = false;
		}

	}

	goToLink(url) {
		return this.appLinks.openExternalPage(url);
	}

}
